// ============================================================================
// Data-access layer (portable core). Thin, typed-ish wrappers over Supabase
// Auth, the `hanja` tables (RLS-guarded), and the SECURITY DEFINER RPCs (§16).
// The UI and the mobile shell call THIS module, never Supabase directly, so the
// backend contract lives in one place.
// ============================================================================

import { getClient } from './supabase.js';
import { ITEM_TYPE } from '../models/constants.js';

// --- Auth -------------------------------------------------------------------

export async function signUp(email, password, emailRedirectTo = (typeof window !== 'undefined' ? window.location.origin : undefined)) {
  // The Supabase project is shared across sibling apps; without an explicit
  // redirect the confirmation email falls back to the project Site URL (another
  // app). Send this app's own origin so a confirmed user lands back here.
  const { data, error } = await getClient().auth.signUp({
    email,
    password,
    options: emailRedirectTo ? { emailRedirectTo } : undefined,
  });
  if (error) throw error;
  return data;
}

export async function signIn(email, password) {
  const { data, error } = await getClient().auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await getClient().auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  const { data } = await getClient().auth.getSession();
  return data.session;
}

export function onAuthChange(handler) {
  const { data } = getClient().auth.onAuthStateChange((_event, session) => handler(session));
  return () => data.subscription.unsubscribe();
}

// --- Profile ----------------------------------------------------------------

/** The signed-in user's hanja profile row (created on signup by handle_new_user). */
export async function getMyProfile() {
  const { data, error } = await getClient()
    .from('profiles')
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Lazily create + return the caller's hanja profile (definer RPC, spec §15.2). */
export async function ensureProfile() {
  const { data, error } = await getClient().rpc('ensure_profile');
  if (error) throw error;
  return data;
}

export async function updateMyProfile(patch) {
  const session = await getSession();
  if (!session) throw new Error('Not signed in');
  const { data, error } = await getClient()
    .from('profiles')
    .update(patch)
    .eq('id', session.user.id)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

// --- Content (approved-only reads via RLS) ----------------------------------

export async function listCharacters({ limit = 100, tier = null } = {}) {
  let q = getClient()
    .from('characters')
    .select('*, character_readings(*), word_characters(count)')
    .order('frequency_rank', { ascending: true, nullsFirst: false })
    .limit(limit);
  if (tier) q = q.eq('edu_tier', tier);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

/** Core vs advanced content counts for the Browse tab labels. */
export async function contentCounts() {
  const cnt = async (table, filters) => {
    let q = getClient().from(table).select('*', { count: 'exact', head: true }).eq('status', 'approved');
    for (const [k, v] of Object.entries(filters)) q = q.eq(k, v);
    const { count } = await q;
    return count ?? 0;
  };
  const [coreChars, rareChars, coreWords, advWords] = await Promise.all([
    cnt('characters', { is_rare: false }),
    cnt('characters', { is_rare: true }),
    cnt('words', { is_advanced: false }),
    cnt('words', { is_advanced: true }),
  ]);
  return { coreChars, rareChars, coreWords, advWords };
}

/** Exact count of approved rows in a content table (for dictionary totals). */
export async function approvedCount(table) {
  const { count, error } = await getClient()
    .from(table)
    .select('*', { count: 'exact', head: true })
    .eq('status', 'approved');
  if (error) throw error;
  return count ?? 0;
}

const WORD_WITH_COMPONENTS =
  '*, word_characters(*, characters(id, char, meaning_en, character_readings(eum, is_primary)))';

export async function listWords({ limit = 100, topikLevel = null } = {}) {
  let q = getClient()
    .from('words')
    .select(WORD_WITH_COMPONENTS)
    .order('frequency_rank', { ascending: true, nullsFirst: false })
    .limit(limit);
  if (topikLevel) q = q.eq('topik_level', topikLevel);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

/**
 * One page of characters for the dictionary grid (spec §10.5.4), ordered by
 * frequency. Server-side paged via range() so the DOM/network stays bounded as
 * content grows. Returns { rows, count } where count is the total visible rows.
 * Includes the per-character approved-word count (word_characters(count)).
 */
export async function pageCharacters({ offset = 0, limit = 60, tier = null, hasWords = false, rare = false } = {}) {
  // hasWords: inner-join word_characters so only characters that build at least
  // one word are returned (word_count = links.length). Otherwise left-join the
  // aggregate count (word_count = [0].count), including word-less characters.
  // rare: is_rare characters build only advanced words (Advanced surface); the
  // core lists pass rare=false.
  const select = hasWords
    ? '*, character_readings(*), word_characters!inner(word_id)'
    : '*, character_readings(*), word_characters(count)';
  let q = getClient()
    .from('characters')
    .select(select, { count: 'exact' })
    .eq('is_rare', rare)
    .order('frequency_rank', { ascending: true, nullsFirst: false })
    .range(offset, offset + limit - 1);
  if (tier) q = q.eq('edu_tier', tier);
  const { data, error, count } = await q;
  if (error) throw error;
  return { rows: data ?? [], count: count ?? 0 };
}

/** Word count for a character row from either pageCharacters shape. */
export function characterWordCount(c) {
  const wcs = c?.word_characters;
  if (!Array.isArray(wcs) || !wcs.length) return 0;
  return ('count' in wcs[0]) ? (wcs[0].count ?? 0) : wcs.length;
}

/** One page of words for the dictionary grid, ordered by frequency. { rows, count }. */
export async function pageWords({ offset = 0, limit = 60, topikLevel = null, advanced = false } = {}) {
  let q = getClient()
    .from('words')
    .select('*, word_characters(*)', { count: 'exact' })
    .eq('is_advanced', advanced)
    .order('frequency_rank', { ascending: true, nullsFirst: false })
    .range(offset, offset + limit - 1);
  if (topikLevel) q = q.eq('topik_level', topikLevel);
  const { data, error, count } = await q;
  if (error) throw error;
  return { rows: data ?? [], count: count ?? 0 };
}

/** Distinct TOPIK levels present in the (visible) word set, ascending — for the Browse filter. */
export async function topikLevels() {
  const { data, error } = await getClient().from('words').select('topik_level');
  if (error) throw error;
  return [...new Set((data ?? []).map((r) => r.topik_level).filter((v) => v != null))]
    .sort((a, b) => a - b);
}

/** One character with its readings (any status for admins; approved for others). */
export async function getCharacter(id) {
  const { data, error } = await getClient()
    .from('characters')
    .select('*, character_readings(*)')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function getWord(id) {
  const { data, error } = await getClient()
    .from('words')
    .select(WORD_WITH_COMPONENTS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Approved words that contain a character (word-network, spec §10.2.2). */
export async function wordNetwork(characterId) {
  const { data, error } = await getClient().rpc('word_network', { p_character: characterId });
  if (error) throw error;
  return data ?? [];
}

/** Characters sharing a component (component family, spec §10.1.1). */
export async function componentFamily(componentId) {
  const { data, error } = await getClient().rpc('component_family', { p_component: componentId });
  if (error) throw error;
  return data ?? [];
}

/** Confusable/similar characters for a character (spec §10.1.3). */
export async function similarCharacters(characterId) {
  const { data, error } = await getClient()
    .from('similar_characters')
    .select('reason, similar:similar_id(*, character_readings(*))')
    .eq('character_id', characterId);
  if (error) throw error;
  return data ?? [];
}

/** Approved example sentences for a specific word. */
export async function sentencesForWord(wordId) {
  const { data, error } = await getClient()
    .from('sentences')
    .select('*')
    .eq('word_id', wordId);
  if (error) throw error;
  return data ?? [];
}

export async function listSentences({ limit = 500 } = {}) {
  const { data, error } = await getClient()
    .from('sentences')
    .select('*')
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

// --- Study loop RPCs (§16) --------------------------------------------------

/** Interleaved due-review queue (spec §10.4.4). */
export async function dueQueue(limit = 20) {
  const { data, error } = await getClient().rpc('due_queue', { p_limit: limit });
  if (error) throw error;
  return data ?? [];
}

/**
 * Record one graded review. The RPC atomically updates the SRS schedule, leech
 * flags, mastery state, stats, XP, and streak (spec §16 record_review).
 */
export async function recordReview({
  itemType,
  itemId,
  skill,
  gameType,
  grade,
  isCorrect,
  responseMs = null,
  recognitionConfidence = null,
}) {
  const { data, error } = await getClient().rpc('record_review', {
    p_item_type: itemType,
    p_item_id: itemId,
    p_skill: skill,
    p_game_type: gameType,
    p_grade: grade,
    p_is_correct: isCorrect,
    p_response_ms: responseMs,
    p_recognition_confidence: recognitionConfidence,
  });
  if (error) throw error;
  return data;
}

/**
 * Synthesize Korean speech via the hanja-tts Edge Function (spec §14.1). The
 * Google key stays server-side; this returns a public audio URL. Throws when
 * the function is unavailable/unconfigured so the caller can fall back.
 */
export async function synthesizeSpeech({ text, voice, rate } = {}) {
  const { data, error } = await getClient().functions.invoke('hanja-tts', {
    body: { text, voice, rate },
  });
  if (error) throw error;
  if (!data?.url) throw new Error(data?.error || 'tts_no_audio');
  return data.url;
}

export async function getMyStats() {
  const { data, error } = await getClient().from('user_stats').select('*').maybeSingle();
  if (error) throw error;
  return data;
}

/** True if the caller is an admin (global is_admin_user via hanja.is_admin). */
export async function isAdmin() {
  const { data, error } = await getClient().rpc('is_admin');
  if (error) return false;
  return data === true;
}

/** Generate approval-gated example sentences for a word via Claude (spec §12.2). */
export async function generateSentences(wordId, count = 2) {
  const { data, error } = await getClient().functions.invoke('hanja-generate', {
    body: { word_id: wordId, count },
  });
  if (error) {
    // Surface the function's typed error (e.g. generation_not_configured) if present.
    const ctxMsg = await error?.context?.json?.().then((j) => j?.error).catch(() => null);
    throw new Error(ctxMsg || error.message || 'generation failed');
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Admin approval queue: pending example sentences with word context (§12.3). */
export async function pendingSentences() {
  const { data, error } = await getClient().rpc('pending_sentences');
  if (error) throw error;
  return data ?? [];
}

/** Admin: approve or reject a pending sentence (§12.1). */
export async function reviewSentence(id, approve) {
  const { data, error } = await getClient().rpc('review_sentence', { p_id: id, p_approve: approve });
  if (error) throw error;
  return data;
}

/** Admin: number of users awaiting activation (for the nav badge). 0 if not admin. */
export async function adminPendingCount() {
  const { data, error } = await getClient().rpc('admin_pending_count');
  if (error) return 0;
  return data ?? 0;
}

/** Admin: list app users (pending accounts first) for the user-management screen. */
export async function adminListUsers(search = null) {
  const { data, error } = await getClient().rpc('admin_list_users', { p_search: search || null });
  if (error) throw error;
  return data ?? [];
}

/** Admin: activate or deactivate a user's access (spec §4; existing set_active RPC). */
export async function adminSetActive(userId, active) {
  const { data, error } = await getClient().rpc('set_active', { p_target: userId, p_active: active });
  if (error) throw error;
  return data;
}

/** Admin: set a user's app role (student/tutor/admin). */
export async function adminSetRole(userId, role) {
  const { data, error } = await getClient().rpc('admin_set_role', { p_target: userId, p_role: role });
  if (error) throw error;
  return data;
}

/** Admin: set a user's membership tier (free/subscription/lifetime). */
export async function adminSetMembership(userId, membership) {
  const { data, error } = await getClient().rpc('admin_set_membership', { p_target: userId, p_membership: membership });
  if (error) throw error;
  return data;
}

/** Guided-path overview: units + lessons with per-lesson progress (spec §6.1). */
export async function curriculum() {
  const { data, error } = await getClient().rpc('curriculum');
  if (error) throw error;
  return data ?? [];
}

/** One lesson's items with content for the introduce→practice flow (spec §6.1). */
export async function lessonDetail(lessonId) {
  const { data, error } = await getClient().rpc('lesson_detail', { p_lesson: lessonId });
  if (error) throw error;
  return data;
}

/** Full-text/trigram dictionary search across hanja + words (spec §10.5.4). */
export async function search(query, filters = {}) {
  const { data, error } = await getClient().rpc('search', {
    p_query: query,
    p_filters: filters,
  });
  if (error) throw error;
  return data ?? [];
}

/** All Hanja that share a sound-reading (음/eum), sorted by payoff (spec §10.1.3 kin). */
export async function readingHub(eum) {
  const { data, error } = await getClient().rpc('reading_hub', { p_eum: eum });
  if (error) throw error;
  return data;
}

/** The browsable list of readings shared by more than one Hanja. */
export async function readingIndex() {
  const { data, error } = await getClient().rpc('reading_index');
  if (error) throw error;
  return data ?? [];
}

/** The caller's personal mnemonic + note for an item, or null (spec §10.3.1). */
export async function getNote(itemType, itemId) {
  const { data, error } = await getClient()
    .from('user_notes')
    .select('mnemonic, note')
    .eq('item_type', itemType)
    .eq('item_id', itemId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Create/update the caller's mnemonic + note for an item (definer upsert RPC). */
export async function upsertNote(itemType, itemId, { mnemonic = null, note = null } = {}) {
  const { data, error } = await getClient().rpc('upsert_note', {
    p_item_type: itemType, p_item_id: itemId, p_mnemonic: mnemonic, p_note: note,
  });
  if (error) throw error;
  return data;
}

// --- Favorites & custom decks (spec §10.5.3) --------------------------------

export async function isFavorite(itemType, itemId) {
  const { data, error } = await getClient().rpc('is_favorite', { p_item_type: itemType, p_item_id: itemId });
  if (error) throw error;
  return !!data;
}

/** Toggle an item in the Favorites deck; returns true if now favorited. */
export async function toggleFavorite(itemType, itemId) {
  const { data, error } = await getClient().rpc('toggle_favorite', { p_item_type: itemType, p_item_id: itemId });
  if (error) throw error;
  return !!data;
}

export async function myDecks() {
  const { data, error } = await getClient().rpc('my_decks');
  if (error) throw error;
  return data ?? [];
}

export async function deckContents(deckId) {
  const { data, error } = await getClient().rpc('deck_contents', { p_deck: deckId });
  if (error) throw error;
  return data ?? [];
}

export async function deckCreate(name, kind = 'custom') {
  const { data, error } = await getClient().rpc('deck_create', { p_name: name, p_kind: kind });
  if (error) throw error;
  return data;
}

export async function deckSetItem(deckId, itemType, itemId, add = true) {
  const { data, error } = await getClient().rpc('deck_set_item', {
    p_deck: deckId, p_item_type: itemType, p_item_id: itemId, p_add: add,
  });
  if (error) throw error;
  return data;
}

export async function deleteDeck(deckId) {
  const { error } = await getClient().rpc('delete_deck', { p_deck: deckId });
  if (error) throw error;
}

// --- Badges (spec §9) -------------------------------------------------------

/** All badges with the caller's earned flag + progress toward each threshold. */
export async function myBadges() {
  const { data, error } = await getClient().rpc('my_badges');
  if (error) throw error;
  return data ?? [];
}

/** Award any newly-earned badges; returns an array of newly-earned codes. */
export async function checkBadges() {
  const { data, error } = await getClient().rpc('check_badges');
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

// --- Insights (spec §10.4) --------------------------------------------------

/** Mastery breakdown, due forecast, and leech list for the caller. */
export async function insights() {
  const { data, error } = await getClient().rpc('insights');
  if (error) throw error;
  return data ?? {};
}

export { ITEM_TYPE };
